import * as React from 'react';
import axios from 'axios';
import {
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
  IconButton,
  CircularProgress,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

interface SubCategory {
  subCategoryId: number;
  name: string;
  description: string;
  categoryId: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Category {
  categoryId: number;
  name: string;
}

const SubCategoryList: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = React.useState<SubCategory[]>([]);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [index, setIndex] = React.useState<number>(1);

  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [categoryId, setCategoryId] = React.useState<number | ''>('');
  const [error, setError] = React.useState('');

  const fetchData = React.useCallback(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/sub-categories?page=${page + 1}&perPage=${rowsPerPage}`)
      .then((res) => {
        setData(res.data.data);
        setTotal(res.data.total);
      })
      .catch(() => alert('Помилка при завантаженні підкатегорій'))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data);
    } catch {
      alert('Помилка при завантаженні категорій');
    }
  };

  React.useEffect(() => {
    fetchData();
    fetchCategories();
  }, [fetchData]);

  const handleDelete = async (id: number) => {
    if (!confirm('Видалити підкатегорію?')) return;
    try {
      await axios.delete(`${API_URL}/sub-categories/${id}`);
      fetchData();
    } catch {
      alert('Помилка при видаленні');
    }
  };

  const handleCreate = async () => {
    if (!name || !categoryId) return;
    setError('');
    try {
      const res = await axios.post(`${API_URL}/sub-categories`, {
        name,
        description,
        categoryId,
        index,
      });
      setData(res.data);
      setOpen(false);
      setName('');
      setDescription('');
      setCategoryId('');
      setIndex(1);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Сталася помилка');
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box sx={{ mx: 0, mt: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h4">Підкатегорії</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Додати підкатегорію
        </Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Назва</TableCell>
            <TableCell>Опис</TableCell>
            <TableCell align="right">Дії</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow
              key={row.subCategoryId}
              sx={{ opacity: row.isActive ? 1 : 0.4 }}
            >
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.description || 'Без опису'}</TableCell>
              <TableCell align="right">
                <IconButton
                  onClick={() =>
                    navigate(`/sub-categories/${row.subCategoryId}`)
                  }
                >
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(row.subCategoryId)}>
                  <DeleteIcon color="error" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 20, 50]}
      />

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Додати підкатегорію</DialogTitle>
        <DialogContent>
          <TextField
            label="Назва"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Опис"
            fullWidth
            margin="normal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            type="number"
            label="Порядок"
            fullWidth
            margin="normal"
            value={index}
            inputProps={{ min: 1, max: 100 }}
            onChange={(e) =>
              setIndex(Math.min(100, Math.max(1, Number(e.target.value))))
            }
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Категорія</InputLabel>
            <Select
              value={categoryId}
              onChange={(e) => setCategoryId(Number(e.target.value))}
              label="Категорія"
            >
              {categories.map((cat) => (
                <MenuItem key={cat.categoryId} value={cat.categoryId}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {error && (
            <Typography color="error" mt={1}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Відміна</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!name || !categoryId}
          >
            Додати
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubCategoryList;
